import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { favoriteAnimation, fetchFavoriteIds, unfavoriteAnimation } from '../api/client';
import { useAuth } from '../auth/AuthContext';

interface FavoritesState {
  /** お気に入り済みの animation id 集合。未ログインなら常に空。 */
  favoritedIds: Set<string>;
  /** トグル後にサーバーから返った likes 数（id ごとの上書き値）。無ければ animation.likes をそのまま使う。 */
  likesById: Record<string, number>;
  /** お気に入りのトグル。未ログインならログイン画面へ遷移させる。
   * currentLikes には呼び出し側が現在表示している likes 数（楽観的更新の基準値）を渡す。 */
  toggleFavorite: (id: string, currentLikes: number) => void;
}

const FavoritesContext = createContext<FavoritesState>({
  favoritedIds: new Set(),
  likesById: {},
  toggleFavorite: () => {},
});

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const { user, login } = useAuth();
  const [favoritedIds, setFavoritedIds] = useState<Set<string>>(new Set());
  const [likesById, setLikesById] = useState<Record<string, number>>({});
  // トグル連打対策。同一 id のリクエストが飛んでいる間は再入を無視する。
  const busyIdsRef = useRef<Set<string>>(new Set());

  // お気に入り済みIDはログイン時のみ分かる。
  useEffect(() => {
    if (!user) {
      setFavoritedIds(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ids = await fetchFavoriteIds();
        if (!cancelled) setFavoritedIds(ids);
      } catch (e) {
        console.error('お気に入り一覧の取得に失敗しました', e);
        if (!cancelled) setFavoritedIds(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const toggleFavorite = (id: string, currentLikes: number) => {
    if (!user) {
      login();
      return;
    }
    if (busyIdsRef.current.has(id)) return;
    busyIdsRef.current.add(id);

    const isFav = favoritedIds.has(id);
    // ロールバック用に、楽観的更新をかける前の状態を控えておく。
    const hadLikesOverride = Object.prototype.hasOwnProperty.call(likesById, id);
    const prevLikesOverride = likesById[id];

    // 楽観的更新: API 応答を待たずに即座に反映する。
    setFavoritedIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(id);
      else next.add(id);
      return next;
    });
    setLikesById((prev) => ({ ...prev, [id]: currentLikes + (isFav ? -1 : 1) }));

    (async () => {
      try {
        const res = isFav ? await unfavoriteAnimation(id) : await favoriteAnimation(id);
        // サーバーが返した正値で上書きする。
        setFavoritedIds((prev) => {
          const next = new Set(prev);
          if (res.favorited) next.add(id);
          else next.delete(id);
          return next;
        });
        setLikesById((prev) => ({ ...prev, [id]: res.likes }));
      } catch (e) {
        console.error('お気に入りの更新に失敗しました', e);
        // 楽観的更新をロールバックする。
        setFavoritedIds((prev) => {
          const next = new Set(prev);
          if (isFav) next.add(id);
          else next.delete(id);
          return next;
        });
        setLikesById((prev) => {
          if (!hadLikesOverride) {
            const next = { ...prev };
            delete next[id];
            return next;
          }
          return { ...prev, [id]: prevLikesOverride };
        });
      } finally {
        busyIdsRef.current.delete(id);
      }
    })();
  };

  const value: FavoritesState = { favoritedIds, likesById, toggleFavorite };

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites(): FavoritesState {
  return useContext(FavoritesContext);
}
